import { AlertCircle, FileText, Upload } from 'lucide-react';
import { type ChangeEvent, type FormEvent, useRef, useState } from 'react';

import { formatFileSize } from '../../pages/documents/documents-format';
import { Button, Card } from '../ui';

const ACCEPTED_FILE_TYPES = '.txt,.md,.docx';
const SUPPORTED_FORMATS_LABEL = 'Textfil (.txt), Markdown (.md) eller Word-dokument (.docx)';
const MAX_FILE_SIZE_LABEL = 'Maxstorlek 5 MB';

interface UploadDocumentFormProps {
  isUploading: boolean;
  onUpload: (file: File, description: string | undefined, reset: () => void) => void;
  onFileSelected: () => void;
  children?: React.ReactNode;
}

export const UploadDocumentForm = ({
  isUploading,
  onUpload,
  onFileSelected,
  children,
}: UploadDocumentFormProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setDescription('');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(event.target.files?.[0] ?? null);
    onFileSelected();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedFile) return;
    const trimmed = description.trim();
    onUpload(selectedFile, trimmed.length > 0 ? trimmed : undefined, reset);
  };

  const selectedFileInfo = selectedFile ? formatFileSize(selectedFile.size) : undefined;

  return (
    <Card>
      <div className="mb-6 flex items-center gap-3">
        <div className="ml-2 mt-2 rounded-lg bg-brand-blue/10 p-2 text-brand-blue">
          <Upload size={24} />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-brand-blue">Ladda upp dokument</h3>
          <p className="text-sm text-brand-blue/60">
            Stöder {SUPPORTED_FORMATS_LABEL}. {MAX_FILE_SIZE_LABEL}.
          </p>
        </div>
      </div>

      {children}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <div className="relative p-1">
            <input
              ref={fileInputRef}
              id="document-file"
              name="file"
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileChange}
              className="block w-full cursor-pointer rounded-lg border border-brand-blue/20 bg-white/5 px-4 py-3 text-brand-blue backdrop-blur-sm transition-all duration-300 file:mr-4 file:rounded-lg file:border-0 file:bg-brand-blue/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-brand-blue hover:file:bg-brand-blue/20 focus:border-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
            />
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-r from-brand-blue/5 to-transparent" />
          </div>
          {selectedFile && (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-brand-blue/10 bg-brand-blue/5 p-3">
              <FileText size={16} className="text-brand-blue/60" />
              <span className="text-sm text-brand-blue">
                {selectedFile.name}
                {selectedFileInfo && (
                  <span className="ml-1 text-brand-blue/60">({selectedFileInfo})</span>
                )}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-2 p-1">
          <label
            htmlFor="document-description"
            className="block text-sm font-medium text-brand-blue"
          >
            Beskrivning (valfritt)
          </label>
          <div className="relative">
            <textarea
              id="document-description"
              name="description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              className="block w-full resize-none rounded-lg border border-brand-blue/20 bg-white/5 px-4 py-3 text-brand-blue backdrop-blur-sm transition-all duration-300 placeholder:text-brand-blue/40 focus:border-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
              placeholder="Sammanfatta dokumentets innehåll för att förbättra AI-agentens förståelse..."
            />
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-r from-brand-blue/5 to-transparent" />
          </div>
        </div>

        <div className="flex flex-col gap-4 p-1 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-xs text-brand-blue/60">
            <AlertCircle size={14} />
            Dokumentet sparas och kan användas av AI-agenten för kontext.
          </p>
          <Button
            type="submit"
            variant="primary"
            disabled={!selectedFile || isUploading}
            className="min-w-[160px]"
          >
            {isUploading ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Laddar upp...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Spara dokument
              </>
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
};
